import { Box, Flex, LoadingSpinner, Text } from '@homebotapp/hb-react-component-catalog'
import { FeedBase, FeedType, ListingCompact } from '../../../api/gqlaxy/gql/generated/graphql'
import { useGetFeed } from '../../../hooks/gallery/useFeed'
import { defineMessages, useIntl } from 'react-intl'
import { GalleryList } from '../GalleryList/GalleryList'
import { useParams } from 'react-router'
import history from '../../../history'
import { useSelector } from 'react-redux'
import { selectClientHomes } from '../../../store/selectors/client'
import { RootState } from '../../../types/rootState'
import { selectHomeValue } from '../../../store/selectors/home/metadata'
import { useTrackingContext } from '../../../providers/tracking'

export interface FeedProps {
  feed: FeedBase
}

export const MSG = defineMessages({
  viewAll: {
    id: 'General.viewAll',
    defaultMessage: 'View all'
  },
  findAPlace: {
    id: 'General.findAPlace',
    defaultMessage: 'Find a place...'
  },
  for: {
    id: 'General.for',
    defaultMessage: 'For'
  },
  recentPriceDrop: {
    id: 'Gallery.priceDrops',
    defaultMessage: 'Price Drops'
  },
  recentPriceDropDescription: {
    id: 'Gallery.priceDropsDescription',
    defaultMessage: 'See places with a reduced asking price'
  },
  savedSearch: {
    id: 'General.savedSearch',
    defaultMessage: 'Saved Search'
  },
  favorites: {
    id: 'Gallery.favorites',
    defaultMessage: 'Favorites'
  },
  recentlySold: {
    id: 'Gallery.recentlySold',
    defaultMessage: 'Recently Sold'
  },
  recentlySoldDescription: {
    id: 'Gallery.recentlySoldDescription',
    defaultMessage: 'Explore to see what the market is like'
  },
  yourStuff: {
    id: 'General.yourStuff',
    defaultMessage: 'Your Stuff'
  },
  localGallery: {
    id: 'General.localGallery',
    defaultMessage: 'Local Gallery'
  },
  forYou: {
    id: 'General.forYou',
    defaultMessage: 'For You'
  },
  help: {
    id: 'Gallery.help',
    defaultMessage: 'Helping you find your next home'
  },
  localGalleries: {
    id: 'General.localGalleries',
    defaultMessage: 'Local Galleries'
  },
  changeLocation: {
    id: 'General.changeLocation',
    defaultMessage: 'Change Location'
  }
})

export const Feed = ({ feed }: FeedProps) => {
  const intl = useIntl()
  const homeValue = useSelector<RootState, number>(selectHomeValue)
  const { trackingClient } = useTrackingContext()

  const { data, isLoading } = useGetFeed({
    feedId: feed?.id,
    options: {
      priceAnchor: homeValue ?? undefined
    }
  })
  const params = useParams<{ customerProfileId: string }>()

  const onViewAll = () => {
    trackingClient?.linkInteraction({
      ui_context: 'Gallery_Feed',
      descriptor: `Clicked on Feed ${feed?.feedType}`,
      action: 'Clicked',
      guid: `3GtcZMbe9HLD7YUKKiAyjg_${feed?.feedType}`
    })
    history.push(`/for-you/${params.customerProfileId}/gallery/${feed.id}`)
  }

  const renderGalleryTile = (titleKey: string, descriptionKey: string) => {
    return (
      <Box width='100%' mt={10}>
        <Flex flex={1} alignItems='center' justifyContent='space-between'>
          <Text m={0} mb={1} mr={1} fontWeight={600} size={['lg', 'xl']}>
            {intl.formatMessage(MSG[titleKey])}
          </Text>
          <Text
            onClick={onViewAll}
            cursor='pointer'
            m={0}
            mb={1}
            mr={1}
            fontWeight={600}
            size={['xs', 'sm']}
            textColor='primary.500'
          >
            {intl.formatMessage(MSG.viewAll)}
          </Text>
        </Flex>
        <Text m={0} size='sm' textColor='neutral.500'>
          {intl.formatMessage(MSG[descriptionKey])}
        </Text>
      </Box>
    )
  }

  if (isLoading) {
    return (
      <Flex justifyContent='center' mt={5}>
        <LoadingSpinner />
      </Flex>
    )
  }

  return (
    <Box mt={10}>
      {renderGalleryTile(feed.feedType, `${feed.feedType}Description`)}
      <Box mt={5}>
        <GalleryList listings={data?.feed.listings.slice(0, 5) as ListingCompact[]} />
      </Box>
    </Box>
  )
}
